// ─── summaryLibrary.js ────────────────────────────────────────────────────────
// Deterministic Condition Report summary engine — a sibling to conditionLibrary.js.
// Generates the three narrative tiers (overall / sector / area) from STRUCTURED
// facts only. No AI, no network: offline-always, testable, editable. Variety comes
// from authored variant pools selected by a stable seed (same report → same wording;
// different site/conditions → different wording). Personalisation from real facts
// (client, location, building age, counts) makes output read bespoke, not templated.

import { RAG_TOKENS, SECTOR_BY_KEY } from './areaConstants';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// 'YYYY-MM-DD' → 'Month YYYY'. '' if unparseable.
function formatMonthYear(iso) {
  const m = (typeof iso === 'string') && iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const monthIdx = parseInt(m[2], 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return '';
  return `${MONTHS[monthIdx]} ${m[1]}`;
}

// Join labels naturally: [a] → 'a'; [a,b] → 'a and b'; [a,b,c] → 'a, b and c'.
function joinList(items) {
  const xs = (items || []).filter(Boolean);
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

// A fact is "present" if not null/undefined and not an empty string. Numbers
// (including 0) count as present.
function factPresent(v) {
  return v !== null && v !== undefined && v !== '';
}

// Small deterministic string hash (djb2) → non-negative integer.
export function hashSeed(str) {
  let h = 5381;
  const s = String(str == null ? '' : str);
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Replace {token} slots from facts. Present facts substitute; absent ones are left
// intact (selection guarantees a chosen variant's required slots are all present).
export function fillTemplate(template, facts) {
  return String(template).replace(/\{(\w+)\}/g, (m, key) => (
    factPresent(facts[key]) ? String(facts[key]) : m
  ));
}

// Filter a pool to variants whose required facts are all present, then pick one
// deterministically from the seed. null if nothing is eligible.
export function selectVariant(pool, facts, seed) {
  const eligible = (pool || []).filter(v => (v.requires || []).every(k => factPresent(facts[k])));
  if (!eligible.length) return null;
  return eligible[hashSeed(seed) % eligible.length];
}

// Assemble the flat fact set from project + view model.
export function buildFacts(project, vm) {
  const p = project || {};
  const meta = p.reportMeta || {};
  const vmMeta = vm.meta || {};
  const as = vm.areaStats || { red: 0, amber: 0, green: 0, total: 0 };
  const ss = vm.stats || { red: 0, amber: 0, green: 0, total: 0 };
  const rag = vm.overallRAG || null;
  const sector = vmMeta.sector || meta.sector || null;
  const risks = (vm.keyRisks || []).map(r => (r.label || '').toLowerCase()).filter(Boolean);

  return {
    client: p.clientName || '',
    siteName: vmMeta.siteName || p.projectName || '',
    location: p.address || '',
    sector,
    sectorLabel: (sector && SECTOR_BY_KEY[sector]) ? SECTOR_BY_KEY[sector].label : null,
    buildingAge: meta.buildingAge || null,
    notableItems: meta.notableItems || null,
    monthYear: formatMonthYear(vmMeta.inspectionDate || meta.inspectionDate),
    overallRAG: rag,
    overallLabel: rag ? RAG_TOKENS[rag].label : '',
    areaRed: as.red, areaAmber: as.amber, areaGreen: as.green, areaTotal: as.total,
    subRed: ss.red, subAmber: ss.amber, subGreen: ss.green, subTotal: ss.total,
    dominantRisk: risks[0] || null,
    riskList: risks.length ? joinList(risks.slice(0, 3)) : null,
    repaintCount: as.red || 0,
  };
}

// ─── Variant pools (authored content — expand phrasings freely; ≥1 low-fact
// variant per tier guarantees a fallback that always renders) ─────────────────

const OVERALL_VARIANTS = {
  red: [
    { requires: ['siteName', 'areaTotal'], text: 'This assessment of {siteName} found {areaRed} of {areaTotal} areas in poor condition, recommended for repaint as soon as possible. The overall site condition is rated Poor.' },
    { requires: ['siteName', 'monthYear', 'areaTotal'], text: 'Higgins assessed {siteName} in {monthYear}. Of {areaTotal} areas inspected, {areaRed} should be repainted as soon as possible and {areaAmber} are worth considering for painting, placing the site in overall Poor condition.' },
    { requires: ['siteName', 'location', 'areaTotal'], text: 'At {siteName} in {location}, {areaRed} of {areaTotal} areas are in poor condition and should be repainted as soon as possible. Overall the site is rated Poor.' },
  ],
  amber: [
    { requires: ['siteName', 'areaTotal'], text: 'Of {areaTotal} areas assessed at {siteName}, {areaAmber} are worth considering for painting and {areaGreen} need no painting at all, giving an overall condition of Fair.' },
    { requires: ['siteName', 'location', 'client', 'monthYear', 'areaTotal'], text: "Higgins assessed {siteName} at {location} for {client} in {monthYear}. Of the site's {areaTotal} areas, {areaRed} should be repainted as soon as possible, {areaAmber} are worth considering for painting, and {areaGreen} need no painting at all — an overall condition of Fair." },
    { requires: ['siteName', 'monthYear', 'areaTotal'], text: 'The {monthYear} assessment of {siteName} rated the site Fair overall. Across {areaTotal} areas, {areaAmber} show early deterioration and are worth considering for painting, while {areaGreen} need no painting at all.' },
  ],
  green: [
    { requires: ['siteName', 'areaTotal'], text: '{siteName} presents in good condition: {areaGreen} of {areaTotal} areas need no painting, and the overall site condition is rated Good.' },
    { requires: ['siteName', 'monthYear', 'areaTotal'], text: 'The {monthYear} assessment of {siteName} found the site in good overall condition, with {areaGreen} of {areaTotal} areas needing no painting and no widespread deterioration.' },
    { requires: ['siteName', 'location', 'areaTotal'], text: 'At {siteName} in {location}, the coatings are generally sound — {areaGreen} of {areaTotal} areas need no painting and the site is rated Good overall.' },
  ],
};

const RISK_SENTENCES = [
  { requires: ['riskList'], text: 'The principal risks identified are {riskList}.' },
  { requires: ['dominantRisk'], text: 'Coating condition across the site is dominated by {dominantRisk}.' },
];

const AGE_SENTENCES = [
  { requires: ['buildingAge'], text: 'The buildings date from the {buildingAge}, and their condition reflects age and exposure.' },
  { requires: ['buildingAge', 'location'], text: 'Typical of {buildingAge} construction in {location}, weathering is most evident on exposed elements.' },
];

const SECTOR_VARIANTS = [
  { requires: ['sectorLabel'], text: 'As a {sectorLabel} site, resident amenity and presentation are priorities; the findings below are grouped to support planned, low-disruption maintenance.' },
  { requires: ['sectorLabel', 'client'], text: 'For {client}, this {sectorLabel} site is assessed with continuity of service in mind — the register below highlights where intervention protects both compliance and appearance.' },
];

const AREA_VARIANTS = {
  red: [
    { requires: ['areaName'], text: '{areaName} is in poor condition and should be repainted as soon as possible.' },
    { requires: ['areaName', 'areaAction'], text: '{areaName} is in poor condition — recommendation: {areaAction}.' },
  ],
  amber: [
    { requires: ['areaName'], text: '{areaName} shows early deterioration; painting is worth considering, though not essential.' },
    { requires: ['areaName', 'areaAction'], text: '{areaName} is sound but showing wear — recommendation: {areaAction}.' },
  ],
  green: [
    { requires: ['areaName'], text: '{areaName} is in good condition and needs no painting.' },
    { requires: ['areaName', 'areaAction'], text: '{areaName} presents well — recommendation: {areaAction}.' },
  ],
};

// ─── Tier generators ──────────────────────────────────────────────────────────

// Overall summary (3–5 sentences). Base tier variant + optional enrichment
// sentences (risks, building age, recommendation) + assessor notable items.
export function generateOverallSummary(facts, override) {
  if (factPresent(override)) return override;
  const rag = facts.overallRAG;
  if (!rag) return '';
  const seed = `${facts.siteName}|${rag}|${facts.monthYear}`;
  const base = selectVariant(OVERALL_VARIANTS[rag], facts, `${seed}|overall`);
  if (!base) return '';
  const parts = [fillTemplate(base.text, facts)];
  if (factPresent(facts.riskList)) {
    const v = selectVariant(RISK_SENTENCES, facts, `${seed}|risk`);
    if (v) parts.push(fillTemplate(v.text, facts));
  }
  if (factPresent(facts.buildingAge)) {
    const v = selectVariant(AGE_SENTENCES, facts, `${seed}|age`);
    if (v) parts.push(fillTemplate(v.text, facts));
  }
  if (facts.repaintCount > 0) {
    parts.push(fillTemplate('Higgins recommends prioritising the areas in poorest condition, with {repaintCount} recommended for repaint as soon as possible.', facts));
  }
  if (factPresent(facts.notableItems)) {
    const ni = String(facts.notableItems).trim();
    parts.push((ni.charAt(0).toUpperCase() + ni.slice(1)).replace(/\.?$/, '.'));
  }
  return parts.join(' ');
}

// Sector lead-in (2–3 sentences). '' if no sector.
export function generateSectorSummary(facts, override) {
  if (factPresent(override)) return override;
  if (!factPresent(facts.sectorLabel)) return '';
  const v = selectVariant(SECTOR_VARIANTS, facts, `${facts.siteName}|${facts.sector}|sector`);
  return v ? fillTemplate(v.text, facts) : '';
}

// Area summary (1–2 sentences). areaFacts merges global facts with area specifics.
export function generateAreaSummary(areaFacts, rag, seed, override) {
  if (factPresent(override)) return override;
  if (!rag) return '';
  const v = selectVariant(AREA_VARIANTS[rag], areaFacts, seed);
  return v ? fillTemplate(v.text, areaFacts) : '';
}

// ─── Top-level: three tiers, honouring stored overrides ──────────────────────
export function buildReportSummaries(project, vm, overrides = {}) {
  const facts = buildFacts(project, vm);
  const meta = (project && project.reportMeta) || {};
  const overallOverride = overrides.overall != null ? overrides.overall : meta.overallSummaryOverride;
  const sectorOverrides = overrides.sectors || meta.sectorSummaryOverride || {};
  const areaOverrides = overrides.areas || meta.areaSummaryOverride || {};

  const overall = generateOverallSummary(facts, overallOverride);

  const sectors = {};
  if (facts.sector) {
    sectors[facts.sector] = generateSectorSummary(facts, sectorOverrides[facts.sector]);
  }

  const areas = {};
  (vm.register || []).forEach((group) => {
    const areaFacts = {
      ...facts,
      areaName: group.area,
      areaAction: (group.rows.find(r => r.action) || {}).action || null,
    };
    const seed = `${facts.siteName}|${group.area}|${group.rag}|area`;
    areas[group.area] = generateAreaSummary(areaFacts, group.rag, seed, areaOverrides[group.area]);
  });

  return { overall, sectors, areas };
}
